// src/pages/Relatorios/RelatorioInstituicaoPage.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  TablePagination,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useParams, useNavigate } from 'react-router-dom';
import {
  obterRelatorioInstituicao,
  obterRelatorioPorPeriodo,
  obterRelatorioCursos,
  obterRelatorioPorClasses,
  obterRelatorioCampanhasPerformance,
  obterRelatorioDemografico,
  obterRelatorioTendencias,
  exportarRelatorio,
  exportarRelatorioDemografico,
  exportarRelatorioCursos,
  exportarRelatorioClasses,
  obterOpcoesFiltroDemografia,
} from '../../services/relatorioService';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const RelatorioInstituicaoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Estados
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    dataInicio: null,
    dataFim: null,
    periodo: 'month',
  });
  const [dadosRelatorio, setDadosRelatorio] = useState({
    geral: null,
    periodo: [],
    cursos: [],
    classes: [],
    campanhas: [],
    demografia: [],
    tendencias: []
  });

  // Estado para filtros da Demografia
  const [filtrosDemografia, setFiltrosDemografia] = useState({
    classe: 'Todas',
    curso: 'Todos',
    status: 'Todas',
    busca: '',
  });

  // Estado para armazenar as opções de filtro
  const [opcoesFiltro, setOpcoesFiltro] = useState({
    tipoInstituicao: null,
    cursos: [],
    classes: []
  });

  // Estados para Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Funções auxiliares para criar parâmetros de consulta
  // Atualizar a função criarParametros no RelatorioInstituicaoPage
  const criarParametros = useCallback((baseParams = {}) => {
    console.log('=== Iniciando criarParametros ===');
    console.log('Estado atual do dadosRelatorio:', dadosRelatorio);
    console.log('baseParams recebidos:', baseParams);
    
    const params = new URLSearchParams();
    
    // Adicionar ID da instituição
    params.append('instituicaoId', id);
    console.log('ID da instituição adicionado:', id);
    
    // Adicionar logo
    const logoPath = dadosRelatorio.geral?.instituicao?.logo;
    console.log('Logo encontrado no estado:', logoPath);
    
    if (logoPath) {
      params.append('logoPath', logoPath);
      console.log('Logo path adicionado aos parâmetros:', logoPath);
    }
    
    // Adicionar outros parâmetros
    if (filtros.dataInicio) {
      const dataInicioStr = filtros.dataInicio.toISOString();
      params.append('dataInicio', dataInicioStr);
      console.log('Data início adicionada:', dataInicioStr);
    }
    
    if (filtros.dataFim) {
      const dataFimStr = filtros.dataFim.toISOString();
      params.append('dataFim', dataFimStr);
      console.log('Data fim adicionada:', dataFimStr);
    }
    
    if (baseParams.includePeriodo && filtros.periodo) {
      params.append('periodo', filtros.periodo);
      console.log('Período adicionado:', filtros.periodo);
    }
    
    if (baseParams.includeDemografia) {
      console.log('Adicionando parâmetros demográficos:', filtrosDemografia);
      if (filtrosDemografia.classe !== 'Todas') {
        params.append('classe', filtrosDemografia.classe);
      }
      if (filtrosDemografia.curso !== 'Todos') {
        params.append('curso', filtrosDemografia.curso);
      }
      if (filtrosDemografia.status !== 'Todas') {
        params.append('status', filtrosDemografia.status);
      }
      if (filtrosDemografia.busca?.trim()) {
        params.append('busca', filtrosDemografia.busca.trim());
      }
    }
    
    const paramsObj = Object.fromEntries(params);
    console.log('Parâmetros finais criados:', paramsObj);
    return params;
  }, [filtros, filtrosDemografia, id, dadosRelatorio.geral?.instituicao?.logo]);

  // Função para carregar as opções de filtro quando o componente montar
  // useEffect para carregar opções de filtro
useEffect(() => {
  const carregarOpcoes = async () => {
    console.log('=== Iniciando carregamento de opções de filtro ===');
    try {
      console.log('Buscando opções para instituição:', id);
      const response = await obterOpcoesFiltroDemografia(id);
      console.log('Opções de filtro recebidas:', response);
      setOpcoesFiltro(response);
    } catch (error) {
      console.error('Erro ao carregar opções:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
    }
  };
  carregarOpcoes();
}, [id]);

// Função principal para atualizar os relatórios
const atualizarRelatorios = useCallback(async () => {
  console.log('=== Iniciando atualização de relatórios ===');
  setLoading(true);
  try {
    console.log('Criando parâmetros para as requisições');
    const paramsBasico = criarParametros();
    const paramsPeriodo = criarParametros({ includePeriodo: true });
    
    console.log('Parâmetros criados:', {
      basico: Object.fromEntries(paramsBasico),
      periodo: Object.fromEntries(paramsPeriodo)
    });

    console.log('Iniciando chamadas paralelas');
    const [
      relatorioGeral,
      relatorioPeriodo,
      relatorioCursos,
      relatorioClasses,
      relatorioCampanhas,
      relatorioDemografia,
      relatorioTendencias
    ] = await Promise.all([
      obterRelatorioInstituicao(id, paramsBasico),
      obterRelatorioPorPeriodo(id, paramsPeriodo),
      obterRelatorioCursos(id, paramsBasico),
      obterRelatorioPorClasses(id, paramsBasico),
      obterRelatorioCampanhasPerformance(id, paramsBasico),
      obterRelatorioDemografico(id, paramsBasico),
      obterRelatorioTendencias(id, paramsPeriodo)
    ]);

    console.log('Dados recebidos:', {
      relatorioGeral,
      relatorioPeriodo,
      relatorioCursos,
      relatorioClasses,
      relatorioCampanhas,
      relatorioDemografia,
      relatorioTendencias
    });

    console.log('Atualizando estado com novos dados');
    setDadosRelatorio({
      geral: relatorioGeral,
      periodo: relatorioPeriodo,
      cursos: relatorioCursos,
      classes: relatorioClasses,
      campanhas: relatorioCampanhas,
      demografia: relatorioDemografia,
      tendencias: relatorioTendencias
    });

    console.log('Estado atualizado com sucesso');
  } catch (error) {
    console.error('Erro ao atualizar relatórios:', {
      message: error.message,
      stack: error.stack,
      response: error.response?.data
    });
  } finally {
    setLoading(false);
    console.log('=== Fim da atualização de relatórios ===');
  }
}, [id, criarParametros]);

// useEffect para carregar os relatórios quando o componente montar
useEffect(() => {
  const carregarDadosIniciais = async () => {
    console.log('=== Iniciando carregamento de dados iniciais ===');
    try {
      // Primeiro carregar dados básicos da instituição
      console.log('Buscando dados básicos da instituição:', id);
      const dadosInstituicao = await obterRelatorioInstituicao(id);
      console.log('Dados básicos recebidos:', dadosInstituicao);
      
      console.log('Estado atual antes da atualização:', dadosRelatorio);
      setDadosRelatorio(prev => {
        console.log('Atualizando estado com dados básicos');
        const novoEstado = {
          ...prev,
          geral: dadosInstituicao
        };
        console.log('Novo estado após dados básicos:', novoEstado);
        return novoEstado;
      });

      // Depois carregar o resto dos dados
      console.log('Iniciando carregamento dos dados completos');
      await atualizarRelatorios();
      console.log('Dados completos carregados com sucesso');
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
    }
    console.log('=== Fim do carregamento de dados iniciais ===');
  };
  
  carregarDadosIniciais();
}, [id, atualizarRelatorios]);
  // Função para exportar o relatório em formato genérico (Excel ou PDF)
  const exportarRelatorioGenerico = async (tipo, dados) => {
    try {
      setLoading(true);
      let dadosParaExportar = dados;
      if (!dadosParaExportar) {
        dadosParaExportar = dadosRelatorio;
      }
      const params = criarParametros();
      const resposta = await exportarRelatorio(id, tipo, params, dadosParaExportar);
      const blob = new Blob([resposta], { 
        type: tipo === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-${id}.${tipo === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Erro ao exportar relatório ${tipo}:`, error);
      alert(error.message || `Erro ao exportar relatório ${tipo}. Por favor, tente novamente.`);
    } finally {
      setLoading(false);
    }
  };
  const exportarParaExcel = () => exportarRelatorioGenerico('excel');
  const exportarParaPDF = () => exportarRelatorioGenerico('pdf');

  // Funções de exportação para Cursos e Classes
  const exportarCursosParaExcel = async () => {
    try {
      setLoading(true);
      const params = criarParametros();

      const response = await exportarRelatorioCursos(id, 'excel', params);

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const timestamp = new Date().getTime();
      const fileName = `relatorio-cursos-${id}-${timestamp}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert(error.message || 'Não foi possível gerar o relatório Excel. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const imprimirCursos = async () => {
    try {
      setLoading(true);
      const params = criarParametros();

      const response = await exportarRelatorioCursos(id, 'pdf', params);

      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      window.open(url, '_blank');

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Erro ao imprimir relatório:', error);
      alert(error.message || 'Não foi possível gerar o PDF. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const exportarClassesParaExcel = async () => {
    try {
      setLoading(true);
      const params = criarParametros();

      const response = await exportarRelatorioClasses(id, 'excel', params);

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const timestamp = new Date().getTime();
      const fileName = `relatorio-classes-${id}-${timestamp}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert(error.message || 'Não foi possível gerar o relatório Excel. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const imprimirClasses = async () => {
    try {
      setLoading(true);
      const params = criarParametros();

      const response = await exportarRelatorioClasses(id, 'pdf', params);

      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      window.open(url, '_blank');

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Erro ao imprimir relatório:', error);
      alert(error.message || 'Não foi possível gerar o PDF. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Componentes para os botões de exportação e impressão
  const BotaoImprimirDemografia = () => (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        startIcon={<PrintIcon />}
        onClick={imprimirDemografia}
        disabled={loading}
      >
        {loading ? 'Gerando PDF...' : 'Imprimir Relatório'}
      </Button>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={exportarDemografiaParaExcel}
        disabled={loading}
        sx={{ ml: 1 }}
      >
        {loading ? 'Gerando Excel...' : 'Exportar Excel'}
      </Button>
    </Box>
  );

  const BotaoImprimirCursos = () => (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        startIcon={<PrintIcon />}
        onClick={imprimirCursos}
        disabled={loading}
      >
        {loading ? 'Gerando PDF...' : 'Imprimir Relatório'}
      </Button>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={exportarCursosParaExcel}
        disabled={loading}
        sx={{ ml: 1 }}
      >
        {loading ? 'Gerando Excel...' : 'Exportar Excel'}
      </Button>
    </Box>
  );

  const BotaoImprimirClasses = () => (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        startIcon={<PrintIcon />}
        onClick={imprimirClasses}
        disabled={loading}
      >
        {loading ? 'Gerando PDF...' : 'Imprimir Relatório'}
      </Button>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={exportarClassesParaExcel}
        disabled={loading}
        sx={{ ml: 1 }}
      >
        {loading ? 'Gerando Excel...' : 'Exportar Excel'}
      </Button>
    </Box>
  );

  // Função para limpar os filtros aplicados e recarregar os relatórios
  const limparFiltros = useCallback(() => {
    setFiltros({
      dataInicio: null,
      dataFim: null,
      periodo: 'month',
    });
    setFiltrosDemografia({
      classe: 'Todas',
      curso: 'Todos',
      status: 'Todas',
      busca: '',
    });
    setPage(0);
    atualizarRelatorios();
  }, [atualizarRelatorios]);

  // Função para imprimir a tabela de pré-inscritos filtrada
  const imprimirDemografia = async () => {
    try {
      setLoading(true);
      
      console.log('=== Iniciando geração do PDF demográfico ===');
      console.log('Estado atual do dadosRelatorio:', dadosRelatorio);
      
      // Usar a função criarParametros para garantir consistência
      const params = criarParametros({ includeDemografia: true });
      console.log('Parâmetros montados:', Object.fromEntries(params));
      
      // Verificar especificamente o logo
      const logoPath = dadosRelatorio.geral?.instituicao?.logo;
      console.log('Caminho do logo:', logoPath);
      
      console.log('Iniciando chamada para API...');
      const response = await exportarRelatorioDemografico(id, 'pdf', params);
      console.log('Resposta recebida da API');
      
      // Verificar se a resposta é válida
      if (!(response instanceof Blob)) {
        console.error('Resposta não é um Blob:', response);
        throw new Error('Resposta inválida do servidor');
      }
  
      console.log('Criando URL do Blob...');
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      console.log('URL do Blob criada:', url);
      
      console.log('Abrindo nova janela...');
      window.open(url, '_blank');
      
      // Limpar URL após um tempo
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        console.log('URL do Blob liberada');
      }, 1000);
  
    } catch (error) {
      console.error('Erro ao gerar relatório:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      alert('Erro ao gerar relatório. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const exportarDemografiaParaExcel = async () => {
    try {
      setLoading(true);

      const params = criarParametros({ includeDemografia: true });
      const response = await exportarRelatorioDemografico(id, 'excel', params);

      if (!response) {
        throw new Error('Não foi possível gerar o relatório');
      }

      // Criar blob com o tipo correto
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Criar nome do arquivo com timestamp para evitar cache
      const timestamp = new Date().getTime();
      const fileName = `relatorio-demografico-${id}-${timestamp}.xlsx`;

      // Usar FileSaver ou método nativo
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // Para IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        // Para outros navegadores
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        
        // Adicionar à página, clicar e remover
        document.body.appendChild(link);
        link.click();
        
        // Pequeno delay antes de remover o link e revogar a URL
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }

    } catch (error) {
      console.error('Erro ao exportar relatório demográfico para Excel:', error);
      // Aqui você pode usar um componente de notificação mais elegante
      alert(error.message || 'Erro ao gerar relatório Excel. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Funções para manipulação de Paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Funções de renderização para cada aba

  // Visão Geral
  const renderVisaoGeral = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total de Campanhas
            </Typography>
            <Typography variant="h5">
              {dadosRelatorio.geral?.estatisticas?.totalCampanhas || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total de Cursos
            </Typography>
            <Typography variant="h5">
              {dadosRelatorio.geral?.estatisticas?.totalCursos || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      {/* Adicione mais cards conforme necessário */}
    </Grid>
  );

  // Distribuição por Cursos
  const renderCursos = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Distribuição por Cursos
      </Typography>
      <BotaoImprimirCursos />
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={dadosRelatorio.cursos}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="nome" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="totalPreInscricoes" fill="#8884d8" name="Total de Pré-inscrições" />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  // Distribuição por Classes
  const renderClasses = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Distribuição por Classes
      </Typography>
      <BotaoImprimirClasses />
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={dadosRelatorio.classes}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="numero" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="totalPreInscricoes" fill="#82ca9d" name="Total de Pré-inscrições" />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  // Performance das Campanhas
  const renderCampanhasPerformance = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Performance das Campanhas
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={dadosRelatorio.campanhas}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="nome" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="aprovadas" fill="#4CAF50" name="Aprovadas" />
          <Bar dataKey="pendentes" fill="#FFC107" name="Pendentes" />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  // Distribuição por Demografia
  const renderDemografia = () => {
    if (!dadosRelatorio.demografia?.length) {
      return (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Distribuição por Sexo
          </Typography>
          <Typography variant="body1">
            Nenhum dado demográfico encontrado para esta instituição.
          </Typography>
        </Paper>
      );
    }

    // Função para aplicar filtros na demografia
    const aplicarFiltrosDemografia = (item) => {
      const { classe, status, busca, curso } = filtrosDemografia;

      // Filtro por Classe
      if (classe !== 'Todas' && !(item.preInscritos || []).some(inscrito => inscrito.classe === classe)) {
        return false;
      }

      // Filtro por Curso
      if (curso !== 'Todos' && !(item.preInscritos || []).some(inscrito => inscrito.curso === curso)) {
        return false;
      }

      // Filtro por Status
      if (status !== 'Todas' && !(item.preInscritos || []).some(inscrito => inscrito.status === status)) {
        return false;
      }

      // Filtro por Busca (Nome ou Email)
      if (busca.trim() !== '') {
        const buscaLower = busca.toLowerCase();
        if (!(item.preInscritos || []).some(inscrito =>
          inscrito.nome.toLowerCase().includes(buscaLower) ||
          inscrito.email.toLowerCase().includes(buscaLower)
        )) {
          return false;
        }
      }

      return true;
    };

    const dadosFiltrados = dadosRelatorio.demografia.filter(aplicarFiltrosDemografia);

    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Distribuição por Sexo
        </Typography>
        <Grid container spacing={2}>
          {/* Gráfico de Pizza */}
          <Grid item xs={12} md={6}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dadosFiltrados}
                  nameKey="genero"
                  dataKey="total"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {dadosFiltrados.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Grid>

          {/* Detalhes por Sexo com Filtros */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Detalhes por Sexo
            </Typography>

            {/* Filtros para Demografia */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {opcoesFiltro.tipoInstituicao === 'universidade' ? (
                // Mostrar filtro de Cursos
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <InputLabel>Curso</InputLabel>
                  <Select
                    label="Curso"
                    name="curso"
                    value={filtrosDemografia.curso}
                    onChange={(e) => {
                      setFiltrosDemografia((prev) => ({
                        ...prev,
                        curso: e.target.value,
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="Todos">Todos</MenuItem>
                    {opcoesFiltro.cursos?.map(curso => (
                      <MenuItem key={curso.id} value={curso.nome}>
                        {curso.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : opcoesFiltro.tipoInstituicao === 'colegio' ? (
                // Mostrar filtro de Classes
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <InputLabel>Classe</InputLabel>
                  <Select
                    label="Classe"
                    name="classe"
                    value={filtrosDemografia.classe}
                    onChange={(e) => {
                      setFiltrosDemografia((prev) => ({
                        ...prev,
                        classe: e.target.value,
                      }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="Todas">Todas</MenuItem>
                    {opcoesFiltro.classes?.map(classe => (
                      <MenuItem key={classe.id} value={classe.numero}>
                        {classe.numero}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              {/* Filtro por Status */}
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  name="status"
                  value={filtrosDemografia.status}
                  onChange={(e) => {
                    setFiltrosDemografia(prev => ({
                      ...prev,
                      status: e.target.value
                    }));
                    setPage(0);
                  }}
                >
                  <MenuItem value="Todas">Todas</MenuItem>
                  <MenuItem value="Aprovada">Aprovada</MenuItem>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  {/* Adicione mais status conforme necessário */}
                </Select>
              </FormControl>

              {/* Barra de Busca */}
              <TextField
                label="Buscar por Nome ou Email"
                name="busca"
                value={filtrosDemografia.busca}
                onChange={(e) => {
                  setFiltrosDemografia((prev) => ({
                    ...prev,
                    busca: e.target.value,
                  }));
                  setPage(0);
                }}
                size="small"
                variant="outlined"
              />
            </Box>

            {/* Botão de Exportação */}
            <BotaoImprimirDemografia />

            {/* Tabela de Pré-Inscritos */}
            {dadosFiltrados.map((item, index) => (
              <Box key={index} sx={{ mb: 4 }}>
                <Typography variant="body1">
                  Gênero: {item.genero}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total: {item.total} | Média de Idade: {item.mediaIdade ? Number(item.mediaIdade).toFixed(1) : 'N/A'} anos
                </Typography>

                {/* Detalhes dos Pré-Inscritos */}
                {item.preInscritos && item.preInscritos.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Detalhes dos Pré-Inscritos:</Typography>
                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader aria-label="tabela de pré-inscritos">
                          <TableHead>
                            <TableRow>
                              <TableCell>Nome</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Telefone</TableCell>
                              <TableCell>Endereço</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Campanha</TableCell>
                              <TableCell>Curso / Classe</TableCell>
                              <TableCell>Período</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {item.preInscritos
                              .filter(inscrito => {
                                const { classe, status, busca, curso } = filtrosDemografia;
                                // Filtro por Classe
                                if (classe !== 'Todas' && inscrito.classe !== classe) return false;
                                // Filtro por Curso
                                if (curso !== 'Todos' && inscrito.curso !== curso) return false;
                                // Filtro por Status
                                if (status !== 'Todas' && inscrito.status !== status) return false;
                                // Filtro por Busca
                                if (busca.trim() !== '') {
                                  const buscaLower = busca.toLowerCase();
                                  if (
                                    !inscrito.nome.toLowerCase().includes(buscaLower) &&
                                    !inscrito.email.toLowerCase().includes(buscaLower)
                                  ) {
                                    return false;
                                  }
                                }
                                return true;
                              })
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((inscrito, idx) => (
                                <TableRow hover tabIndex={-1} key={idx}>
                                  <TableCell>{inscrito.nome}</TableCell>
                                  <TableCell>{inscrito.email}</TableCell>
                                  <TableCell>{inscrito.telefone || 'N/A'}</TableCell>
                                  <TableCell>{inscrito.endereco || 'N/A'}</TableCell>
                                  <TableCell>{inscrito.status}</TableCell>
                                  <TableCell>{inscrito.campanha}</TableCell>
                                  <TableCell>
                                    {inscrito.curso || inscrito.classe || 'N/A'}
                                  </TableCell>
                                  <TableCell>{inscrito.periodo || 'N/A'}</TableCell>
                                </TableRow>
                              ))
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {/* Paginação */}
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={item.preInscritos.filter(inscrito => {
                          const { classe, status, busca, curso } = filtrosDemografia;
                          // Filtro por Classe
                          if (classe !== 'Todas' && inscrito.classe !== classe) return false;
                          // Filtro por Curso
                          if (curso !== 'Todos' && inscrito.curso !== curso) return false;
                          // Filtro por Status
                          if (status !== 'Todas' && inscrito.status !== status) return false;
                          // Filtro por Busca
                          if (busca.trim() !== '') {
                            const buscaLower = busca.toLowerCase();
                            if (
                              !inscrito.nome.toLowerCase().includes(buscaLower) &&
                              !inscrito.email.toLowerCase().includes(buscaLower)
                            ) {
                              return false;
                            }
                          }
                          return true;
                        }).length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Paper>
                  </Box>
                )}
              </Box>
            ))}
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Renderiza as Tendências
  const renderTendencias = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Tendências
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={dadosRelatorio.tendencias}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="data" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="total" stroke="#8884d8" name="Total de Pré-inscrições" />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );

  // Função para renderizar um indicador de carregamento
  const renderLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Botão "Voltar" */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')} // Substitua '/dashboard' pela rota correta do seu dashboard
        >
          Voltar ao Dashboard
        </Button>
      </Box>

      {/* Seção de Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Relatório: {dadosRelatorio.geral?.instituicao?.nome || 'Instituição'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          {/* Filtro de Data Início */}
          <DatePicker
            label="Data Início"
            value={filtros.dataInicio}
            onChange={(date) => setFiltros({ ...filtros, dataInicio: date })}
            renderInput={(params) => <TextField {...params} />}
          />
          {/* Filtro de Data Fim */}
          <DatePicker
            label="Data Fim"
            value={filtros.dataFim}
            onChange={(date) => setFiltros({ ...filtros, dataFim: date })}
            renderInput={(params) => <TextField {...params} />}
          />
          {/* Filtro de Período */}
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Período</InputLabel>
            <Select
              label="Período"
              value={filtros.periodo}
              onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })}
            >
              <MenuItem value="day">Diário</MenuItem>
              <MenuItem value="month">Mensal</MenuItem>
              <MenuItem value="year">Anual</MenuItem>
            </Select>
          </FormControl>
          {/* Botão de Atualizar */}
          <Button
            variant="contained"
            onClick={atualizarRelatorios}
            disabled={loading}
          >
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
          {/* Botão de Exportar Excel */}
          <Button
            variant="outlined"
            onClick={exportarParaExcel}
            startIcon={<DownloadIcon />}
            disabled={loading}
          >
            Exportar Excel
          </Button>
          {/* Botão de Exportar PDF */}
          <Button
            variant="outlined"
            onClick={exportarParaPDF}
            startIcon={<DownloadIcon />}
            disabled={loading}
          >
            Exportar PDF
          </Button>
          {/* Botão de Limpar Filtros */}
          <Button
            variant="outlined"
            onClick={limparFiltros}
            startIcon={<ClearIcon />}
            disabled={
              !filtros.dataInicio &&
              !filtros.dataFim &&
              filtros.periodo === 'month' &&
              filtrosDemografia.classe === 'Todas' &&
              filtrosDemografia.curso === 'Todos' &&
              filtrosDemografia.status === 'Todas' &&
              filtrosDemografia.busca === ''
            }
          >
            Limpar Filtro
          </Button>
        </Box>
      </Paper>

      {/* Seção de Abas */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Visão Geral" />
        <Tab label="Cursos" />
        <Tab label="Classes" />
        <Tab label="Campanhas" />
        <Tab label="Estatisticas" />
        <Tab label="Tendências" />
      </Tabs>

      {/* Conteúdo das Abas */}
      <Box sx={{ mt: 2 }}>
        {loading || !dadosRelatorio.geral ? renderLoading() : (
          <>
            {activeTab === 0 && renderVisaoGeral()}
            {activeTab === 1 && renderCursos()}
            {activeTab === 2 && renderClasses()}
            {activeTab === 3 && renderCampanhasPerformance()}
            {activeTab === 4 && renderDemografia()}
            {activeTab === 5 && renderTendencias()}
          </>
        )}
      </Box>
    </Box>
  );
};

export default RelatorioInstituicaoPage;
